import { ParamMap } from '@angular/router';
import { IBoardwalkCustomParams } from 'src/app/domain/interfaces/crossell/interface-cross-sell-component';
import { FunnelFlow } from 'src/app/domain/models/funnel/register-funnels/funnel-flow-register';

const SUB1_PREFIX = 'US-';
// Tracking ids travel through URLs; anything outside these patterns is dropped.
const SUB_TRACKING_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const NUMERIC_ID_PATTERN = /^[0-9]{1,32}$/;
// Funnel redirects interpolate missing values as the literal text "undefined" / "null".
const EMPTY_LITERALS = new Set(['undefined', 'null']);

export type BoardwalkQueryParamSource = Pick<ParamMap, 'get'>;

export function createEmptyBoardwalkParams(): IBoardwalkCustomParams {
  return {
    sub1: '',
    affiliateId: '',
    offerId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
  };
}

export function toBoardwalkString(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
    return '';
  }
  const text = String(value).trim();
  return EMPTY_LITERALS.has(text.toLowerCase()) ? '' : text;
}

export function toBoardwalkId(value: unknown): string {
  const text = toBoardwalkString(value);
  return NUMERIC_ID_PATTERN.test(text) ? text : '';
}

// Always "US-<sub1>", the same value the funnels put in the page URL (URL params win in Boardwalk).
export function toBoardwalkSub1(value: unknown): string {
  let text = toBoardwalkString(value);
  if (text.toUpperCase().startsWith(SUB1_PREFIX)) {
    text = toBoardwalkString(text.substring(SUB1_PREFIX.length));
  }
  return SUB_TRACKING_PATTERN.test(text) ? `${SUB1_PREFIX}${text}` : '';
}

// Maps the legacy query string that the funnels send to the Decline / Duplicate User pages.
export function buildBoardwalkParamsFromQueryParams(params?: BoardwalkQueryParamSource | null): IBoardwalkCustomParams {
  const get = (key: string) => params?.get(key);
  return {
    sub1: toBoardwalkSub1(get('sub1')),
    affiliateId: toBoardwalkId(get('sub3')),
    // Some Duplicate User redirects only send ?oid=.
    offerId: toBoardwalkId(get('sub5')) || toBoardwalkId(get('oid')),
    firstName: toBoardwalkString(get('first_name')),
    lastName: toBoardwalkString(get('last_name')),
    email: toBoardwalkString(get('email')),
    phone: toBoardwalkString(get('phone')),
    street: toBoardwalkString(get('address')),
    city: toBoardwalkString(get('city')),
    state: toBoardwalkString(get('state')),
    zip: toBoardwalkString(get('zip')),
  };
}

// Thank You page: the funnel state is still in memory, so it is read directly.
export function buildBoardwalkParamsFromFunnelFlow(flow?: FunnelFlow | null): IBoardwalkCustomParams {
  const user = flow?.UserTemporary;
  const address = user?.addresses?.[0];
  return {
    sub1: toBoardwalkSub1(flow?.UserEverflowEvent?.EverflowSub1),
    affiliateId: toBoardwalkId(flow?.EverflowAffiliate?.everflowAffiliateId),
    offerId: toBoardwalkId(flow?.SubscriptionProduct?.funnel?.offerId),
    firstName: toBoardwalkString(user?.firstName),
    lastName: toBoardwalkString(user?.lastName),
    email: toBoardwalkString(user?.email),
    phone: toBoardwalkString(user?.phone),
    street: toBoardwalkString(address?.streetAddress),
    city: toBoardwalkString(address?.city),
    state: toBoardwalkString(address?.state),
    zip: toBoardwalkString(address?.zip),
  };
}
